import { useMemo, useState } from 'react';
import { useT } from '../../lib/i18n';
import { formatDuration, useMoney } from '../../lib/format';
import { ScreenHeading, Skeleton } from '../shell/PortalShell';
import type { Catalog, Service, ServiceCategory } from '../../lib/types';

/**
 * Service picker — two levels.
 *
 * Level 1 is a grid of category cards led by the photo the tenant uploaded in
 * admin (pos_category_images). Every card uses the same fixed square image box
 * regardless of the source file, so a tenant who mixes portrait and landscape
 * uploads still gets an even grid. Level 2 is the services inside the chosen
 * category.
 *
 * Search cuts across both levels: typing flattens every category into one list,
 * because someone hunting for "beard trim" doesn't know or care which category
 * it lives in. Clearing the box returns to whichever level they were on.
 *
 * Selection lives in the parent, so drilling in and out never loses a pick.
 */
export function ServicesStep({
  catalog,
  loading,
  selected,
  maxServices,
  onToggle,
}: {
  catalog: Catalog | null;
  loading: boolean;
  selected: Service[];
  maxServices: number;
  onToggle: (service: Service) => void;
}): JSX.Element {
  const { t, lang } = useT();
  const [query, setQuery] = useState('');
  const [activeKey, setActiveKey] = useState<string | null>(null);

  const selectedIds = useMemo(() => new Set(selected.map((s) => s.item_id)), [selected]);
  const atLimit = selected.length >= maxServices;

  const categories = catalog?.categories ?? [];
  const keyOf = (category: ServiceCategory, index: number): string =>
    String(category.id ?? `c${index}`);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || !catalog) return null;
    return catalog.categories
      .flatMap((c) => c.services)
      .filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          (s.name_ar ?? '').toLowerCase().includes(q) ||
          s.code.toLowerCase().includes(q),
      );
  }, [query, catalog]);

  const active = useMemo(
    () => categories.find((c, i) => keyOf(c, i) === activeKey) ?? null,
    [categories, activeKey],
  );

  if (loading) {
    return (
      <div>
        <ScreenHeading title={t('choose_services')} subtitle={t('choose_services_sub')} />
        <Skeleton rows={4} />
      </div>
    );
  }

  if (!catalog || categories.length === 0) {
    return (
      <div className="bk-rise">
        <ScreenHeading title={t('choose_services')} />
        <p className="bk-card p-6 text-center text-sm text-zinc-500">{t('no_services')}</p>
      </div>
    );
  }

  const catName = (c: ServiceCategory): string =>
    (lang === 'ar' && c.name_ar ? c.name_ar : c.name) || c.name;

  const searching = matches !== null;
  const showingCategories = !searching && active === null;

  return (
    <div className="bk-rise">
      <ScreenHeading
        title={showingCategories ? t('choose_category') : t('choose_services')}
        subtitle={showingCategories ? t('choose_category_sub') : t('choose_services_sub')}
      />

      <div className="relative mb-4">
        <input
          type="search"
          className="bk-input ps-11"
          placeholder={t('search_services')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label={t('search_services')}
        />
        <svg
          className="pointer-events-none absolute top-1/2 -translate-y-1/2 text-zinc-400 [inset-inline-start:0.9rem]"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden
        >
          <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
          <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </div>

      {atLimit && (
        <p className="mb-3 rounded-xl bg-amber-50 px-4 py-2.5 text-xs text-amber-800">
          {t('max_services_reached')}
        </p>
      )}

      {searching ? (
        matches.length === 0 ? (
          <p className="bk-card p-6 text-center text-sm text-zinc-500">{t('no_search_results')}</p>
        ) : (
          <div className="space-y-2">
            {matches.map((service) => (
              <ServiceRow
                key={service.item_id}
                service={service}
                selected={selectedIds.has(service.item_id)}
                disabled={atLimit && !selectedIds.has(service.item_id)}
                onToggle={onToggle}
              />
            ))}
          </div>
        )
      ) : active ? (
        <div>
          {/* Back to the category grid. Sits above the list so it is the first
              thing reachable by keyboard after the search box. */}
          <button
            type="button"
            onClick={() => setActiveKey(null)}
            className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-zinc-600 hover:text-zinc-900"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden
              className="bk-back-icon"
            >
              <path
                d="M15 18l-6-6 6-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {t('all_categories')}
          </button>

          <h2 className="mb-3 text-base font-semibold text-zinc-900">{catName(active)}</h2>

          {active.services.length === 0 ? (
            <p className="bk-card p-6 text-center text-sm text-zinc-500">{t('category_empty')}</p>
          ) : (
            <div className="space-y-2">
              {active.services.map((service) => (
                <ServiceRow
                  key={service.item_id}
                  service={service}
                  selected={selectedIds.has(service.item_id)}
                  disabled={atLimit && !selectedIds.has(service.item_id)}
                  onToggle={onToggle}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {categories.map((category, index) => (
            <CategoryCard
              key={keyOf(category, index)}
              category={category}
              name={catName(category)}
              chosen={category.services.filter((s) => selectedIds.has(s.item_id)).length}
              onOpen={() => setActiveKey(keyOf(category, index))}
            />
          ))}
        </div>
      )}

      <span className="bk-sr" aria-live="polite">
        {selected.length} {t('selected')}
      </span>
    </div>
  );
}

/**
 * One category tile.
 *
 * The photo sits in `.bk-cat-media` — a fixed square box with `object-cover` —
 * so every card is the same height whatever the upload's dimensions.
 * Categories without a photo get an identically-sized tinted placeholder
 * rather than a shorter card, which would leave holes in the grid.
 * `.bk-media-tile` drops the tile's padding so the photo reaches the edge.
 */
function CategoryCard({
  category,
  name,
  chosen,
  onOpen,
}: {
  category: ServiceCategory;
  name: string;
  chosen: number;
  onOpen: () => void;
}): JSX.Element {
  const { t } = useT();
  const count = category.services.length;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="bk-tile bk-media-tile flex flex-col text-start"
    >
      <span className="relative block w-full">
        {category.image ? (
          <img src={category.image} alt="" loading="lazy" className="bk-cat-media" />
        ) : (
          <span
            className="bk-cat-media grid place-items-center text-3xl font-semibold"
            style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
            aria-hidden
          >
            {name.trim().charAt(0).toUpperCase()}
          </span>
        )}
        {chosen > 0 && (
          <span
            className="absolute top-2 grid h-6 min-w-6 place-items-center rounded-full px-1.5 text-xs font-semibold text-white [inset-inline-end:0.5rem]"
            style={{ background: 'var(--accent)' }}
          >
            {chosen}
          </span>
        )}
      </span>
      <span className="flex flex-1 flex-col gap-0.5 p-3">
        <span className="line-clamp-2 text-sm font-semibold text-zinc-900">{name}</span>
        <span className="text-xs text-zinc-500">
          {count} {count === 1 ? t('service_count_one') : t('service_count_many')}
        </span>
      </span>
    </button>
  );
}

function ServiceRow({
  service,
  selected,
  disabled,
  onToggle,
}: {
  service: Service;
  selected: boolean;
  disabled: boolean;
  onToggle: (service: Service) => void;
}): JSX.Element {
  const { lang, pick } = useT();
  const money = useMoney();
  return (
    <button
      type="button"
      className="bk-tile flex items-center gap-3 p-3 disabled:opacity-40"
      aria-pressed={selected}
      disabled={disabled}
      onClick={() => onToggle(service)}
    >
      {service.image ? (
        <img src={service.image} alt="" className="h-12 w-12 shrink-0 rounded-xl object-cover" loading="lazy" />
      ) : (
        <span
          className="h-12 w-12 shrink-0 rounded-xl"
          style={{ background: service.color ?? '#f4f4f5' }}
          aria-hidden
        />
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-zinc-900">{pick(service)}</span>
        <span className="block text-xs text-zinc-500">
          {formatDuration(service.duration_minutes, lang)}
        </span>
      </span>
      <span className="shrink-0 text-sm font-semibold text-zinc-900">
        {money(service.price)}
      </span>
      <span
        className={`grid h-6 w-6 shrink-0 place-items-center rounded-full border-2 transition ${
          selected ? 'border-transparent' : 'border-zinc-300'
        }`}
        style={selected ? { background: 'var(--accent)' } : undefined}
        aria-hidden
      >
        {selected && (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
            <path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
    </button>
  );
}
